import React, { useState } from "react";
import { BiCalendar } from "react-icons/bi";
import { IoEyeOutline } from "react-icons/io5";
import { WiTime4 } from "react-icons/wi";
import { NavLink } from "react-router-dom";
import { useGetAllPopUpQuery } from "../../redux/popUpApi";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import dayjs from "dayjs";
import { getPopUpStatus } from "../../utils/Utils";
import { GoDotFill } from "react-icons/go";
import { RiDeleteBin5Line } from "react-icons/ri";
import { FaRegEdit } from "react-icons/fa";
import { useDebounce } from "use-debounce";

const AnnouncementPopup = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 1500);
  const { data, isLoading, isFetching } = useGetAllPopUpQuery({
    page,
    search: debouncedSearch,
  });
  const popUp = data?.popUp;

  const handlePrev = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const handleNext = () => {
    if (page < data?.totalPages) setPage((prev) => prev + 1);
  };

  return (
    <div className="flex flex-col lg:gap-10 md:gap-6 gap-4 bg-white rounded-xl lg:p-4 p-3 h-full">
      <div className="flex justify-between items-center">
        <p className="text-[#05004E] font-semibold lg:text-[1.8rem] md:text-[1.4rem] text-[1.2rem]">
          Announcements
        </p>
        <div className="flex  items-center">
          <NavLink
            to={"/announcement-popup/add-popup"}
            className="lg:w-[12rem] md:w-[10rem] w-max bg-[#00643b] text-center lg:py-3 md:py-2 md:px-0 px-2 py-1.5 lg:text-[1rem] md:text-[.9rem] text-[.75rem] md:rounded-xl rounded-lg font-medium text-white"
          >
            + Add Announcement
          </NavLink>
        </div>
      </div>
      {isFetching || isLoading ? (
        <PopUpCardSkeleton />
      ) : (
        <div className=" flex flex-col gap-3">
          {popUp?.map((item, index) => (
            <AnnouncementCard key={index} {...item} />
          ))}
        </div>
      )}
      {popUp?.length > 0 && (
        <div className=" flex justify-between items-center">
          <div className=" text-[#0000004f]">
            Showing record page {data?.currentPage} 0f {data?.totalPages}
          </div>
          <div className=" flex items-center gap-4">
            <div
              onClick={handlePrev}
              disabled={page === 1}
              className={`bg-[#efefef] p-2  rounded-xl ${
                page === 1 ? " cursor-not-allowed opacity-50" : "cursor-pointer"
              }`}
            >
              <IoIosArrowBack className="size-6" />
            </div>
            {data?.currentPage}
            <div
              onClick={handleNext}
              disabled={page === data?.totalPages}
              className={`bg-[#efefef] p-2  rounded-xl ${
                page === data?.totalPages
                  ? " cursor-not-allowed opacity-50"
                  : "cursor-pointer"
              }`}
            >
              <IoIosArrowForward className="size-6" />
            </div>
          </div>
        </div>
      )}
      {popUp?.length === 0 && (
        <div className=" text-center w-full lg:text-[3rem] md:text-[2rem] text-[1.4rem] font-semibold text-[#05004E]">
          No PopUp Found
        </div>
      )}
    </div>
  );
};

const AnnouncementCard = ({
  _id,
  media,
  title,
  description,
  startDate,
  endDate,
}) => {
  const STATUS_COLORS = {
    upcoming: "bg-[#facc1520] text-[#facc15]", // yellow
    live: "bg-[#E6F9ED] text-[#2BB673]", // green
    expired: "bg-[#9ca3af20] text-[#9ca3af]", // gray
    invalid: " bg-[#6b728020] text-[#6b7280]", // dark gray
  };
  return (
    <div className="flex md:flex-row flex-col items-start gap-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition">
      {/* Image */}
      <img
        src={media?.mediaUrl}
        alt={title}
        className="md:w-32 md:h-24 sm:h-32 w-full object-cover rounded-lg flex-shrink-0"
      />

      {/* Content */}
      <div className="flex flex-col gap-2 w-full">
        <div className=" flex items-center gap-2 ">
          <h3 className="font-semibold grow text-[#2B3674] text-lg">{title}</h3>
          <div className=" flex items-center gap-3 shrink-0">
            <NavLink to={`/announcement-popup/update-popup/${_id}`}>
              <FaRegEdit className=" text-red-500 size-5" />
            </NavLink>
          </div>
        </div>
        <p className="text-gray-500 text-sm ">{description}</p>

        {/* Footer */}
        <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
          <span className="flex items-center gap-1">
            <BiCalendar className="w-4 h-4" />
            {dayjs(startDate).format("YYYY-MM-DD")} -{" "}
            {dayjs(endDate).format("YYYY-MM-DD")}
          </span>

          <span
            className={`flex items-center gap-1 ${
              STATUS_COLORS[getPopUpStatus(startDate, endDate)]
            } font-medium px-2 py-[2px] rounded-full capitalize`}
          >
            <GoDotFill />
            {getPopUpStatus(startDate, endDate)}
          </span>
        </div>
      </div>
    </div>
  );
};

const PopUpCardSkeleton = () => {
  return (
    <div className="flex flex-col gap-3">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="flex md:flex-row flex-col items-start gap-4 bg-white dark:bg-[#121212] rounded-xl shadow-sm border border-gray-100 dark:border-[#2a2a2a] p-4 animate-pulse"
        >
          {/* Image skeleton */}
          <div className=" w-full md:w-32 h-24 rounded-lg bg-gray-200 dark:bg-[#2a2a2a]" />

          {/* Text section */}
          <div className="md:flex-1 w-full space-y-3">
            {/* Title */}
            <div className="h-4 w-1/3 bg-gray-200 dark:bg-[#2a2a2a] rounded"></div>
            {/* Description */}
            <div className="h-3 w-2/3 bg-gray-200 dark:bg-[#2a2a2a] rounded"></div>
            <div className="h-3 w-1/2 bg-gray-200 dark:bg-[#2a2a2a] rounded"></div>
            {/* Date + Status */}
            <div className="flex items-center gap-2 pt-2">
              <div className="h-3 w-24 bg-gray-200 dark:bg-[#2a2a2a] rounded"></div>
              <div className="h-3 w-12 bg-gray-200 dark:bg-[#2a2a2a] rounded-full"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AnnouncementPopup;
